/* eslint-disable react/prop-types */

import React, { useCallback, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import saveAs from 'file-saver';
import axios from 'axios';
import * as Constants from '../../Constant';

import { GridExporter } from '@devexpress/dx-react-grid-export';
import {
  Grid,
  Table,
  TableHeaderRow,
  TableEditColumn,
  PagingPanel,
  Toolbar,
  SearchPanel,
  TableSelection,
  DragDropProvider,
  TableColumnReordering,
  TableColumnResizing,
  ColumnChooser,
  TableColumnVisibility,
  ExportPanel,
  TableInlineCellEditing
} from '@devexpress/dx-react-grid-material-ui';
import {
  EditingState,
  SortingState,
  IntegratedSorting,
  PagingState,
  IntegratedPaging,
  SearchState,
  IntegratedSelection,
  SelectionState,
  DataTypeProvider,
  IntegratedFiltering
} from '@devexpress/dx-react-grid';

import Paper from '@mui/material/Paper';

import useFetchVendorRecords from '../hooks/useFetchVendorRecords';
import useFetchKeyPayEmployeeRecords from '../hooks/useFetchKeyPayEmployeeRecords';
import employeeService from '../../../services/EmployeeService';
import { toast } from 'react-hot-toast';
import { hasValidationErrors, INLINE_COLUMNS, isLinkEmployeeChange, validateChanges } from '../helper/helper';

import { useLocalStorage } from 'react-use';
import CustomToolbarRoot from './customTemplate/CustomToolbarRoot';
import KebabCommandCell from './customTemplate/CustomCommandCell';
import HeaderFilterCell, { getRowCellValue, HeaderFilterContext } from './customTemplate/HeaderFilterCell';
import { Popup, PopupEditing } from './UserPopupEditing';
import { FocusableCell, LookupFormatter } from './UserTableInlineEditing';
import ResponsiveTableContainer, { StickyTableHead } from '../../common/ResponsiveTableContainer';

const getRowId = (row) => row.id;

const onSave = (workbook) => {
  workbook.xlsx.writeBuffer().then((buffer) => {
    // eslint-disable-next-line no-undef
    saveAs(new Blob([buffer], { type: 'application/octet-stream' }), 'DataGrid.xlsx');
  });
};

const ActiveStatusProvider = (props) => {
  return <DataTypeProvider formatterComponent={ActiveStatusFormatter} {...props} />;
};

const ActiveStatusFormatter = ({ value }) => {
  const isActive = value === 'Active';

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
      <span
        style={{
          height: '8px',
          width: '8px',
          borderRadius: '50%',
          backgroundColor: isActive ? 'green' : 'red'
        }}
      ></span>
      <span>{value}</span>
    </div>
  );
};

// A single click is left to TableInlineCellEditing. Double-clicking a data row
// switches from inline editing to the full popup editor.
const ClickableUserRow = ({ row, onOpen, ...restProps }) => {
  const handleDoubleClick = (event) => {
    if (
      !row ||
      event.target.closest('button, a, input[type="checkbox"], [role="checkbox"], [role="button"], [role="menuitem"]')
    )
      return;
    onOpen(getRowId(row));
  };

  const handleKeyDown = (event) => {
    if (event.currentTarget !== event.target) return;
    if (!row || (event.key !== 'Enter' && event.key !== ' ')) return;
    event.preventDefault();
    onOpen(getRowId(row));
  };

  return (
    <Table.Row
      {...restProps}
      row={row}
      tabIndex={row ? 0 : undefined}
      onDoubleClick={handleDoubleClick}
      onKeyDown={handleKeyDown}
      aria-label={row ? `Open ${row.DisplayName || 'user'} details` : undefined}
      style={{
        ...restProps.style,
        cursor: row ? 'pointer' : restProps.style?.cursor
      }}
    />
  );
};

const UserTable = ({ rows, setRows, vendorRefs, taxTypeRecords, userRoleRecords, itemRefs }) => {
  const [userTabIndex, setUserTabIndex] = useState(0);
  const { vendorRecords } = useFetchVendorRecords();
  const { keyPayEmployeeRecords } = useFetchKeyPayEmployeeRecords();
  const [selectedEmployeeToRegister, setSelectedEmployeeToRegister] = useState();

  const [columns] = useState([
    { name: 'DisplayName', title: 'Display Name' },
    { name: 'email', title: 'Email' },
    { name: 'workEmail', title: 'Work Email' },
    { name: 'Phone', title: 'Phone' },
    { name: 'userRole', title: 'User Role' },
    {
      name: 'vendorRef',
      title: 'Supplier',
      getCellValue: (row) => (row.vendorRef ? row.vendorRef.name : undefined)
    },
    {
      name: 'itemRef',
      title: 'Item',
      getCellValue: (row) => (row.itemRef ? row.itemRef.name : undefined)
    },
    {
      name: 'taxType',
      title: 'Tax Type',
      getCellValue: (row) => (row.taxType ? row.taxType.name : undefined)
    },
    { name: 'jobTitle', title: 'Job Title' },
    { name: 'KeyPayEmployeeId', title: 'External Id' },
    { name: 'hourRate', title: 'Hour Rate' },
    { name: 'employeeStatus', title: 'Status', getCellValue: (row) => (row?.portalAccess ? 'Active' : 'Inactive') }
  ]);
  const [columnWidths, setColumnWidths] = useState([
    { columnName: 'DisplayName', width: 180 },
    { columnName: 'email', width: 180 },
    { columnName: 'workEmail', width: 180 },
    { columnName: 'Phone', width: 180 },
    { columnName: 'userRole', width: 180 },
    { columnName: 'vendorRef', width: 180 },
    { columnName: 'itemRef', width: 180 },
    { columnName: 'taxType', width: 180 },
    { columnName: 'jobTitle', width: 180 },
    { columnName: 'KeyPayEmployeeId', width: 180 },
    { columnName: 'hourRate', width: 180 },
    { columnName: 'employeeStatus', width: 180 }
  ]);

  const [sorting, setSorting] = useState([{ columnName: 'DisplayName', direction: 'asc' }]);
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useLocalStorage('userRowsPerPage', 25);
  const [pageSizes] = useState([25, 50, 75]);
  const [searchValue, setSearchState] = useState('');
  const [selection, setSelection] = useState([]);

  const [columnOrder, setColumnOrder] = useState([
    'DisplayName',
    'email',
    'userRole',
    'Phone',
    'vendorRef',
    'itemRef',
    'taxType',
    'jobTitle',
    'KeyPayEmployeeId',
    'hourRate',
    'employeeStatus',
    'workEmail'
  ]);

  const [hiddenColumnNames, setHiddenColumnNames] = useLocalStorage('userHiddenColumns', [
    'hourRate',
    'KeyPayEmployeeId'
  ]);

  const [selectedUserType, setSelectedUserType] = useState({ value: '', label: '' });
  const [approvalRequired, setApprovalRequired] = useState(false);
  const [selectedApprover, setSelectedApprover] = useState({ value: '', label: '', id: '' });
  const [portalAccessDialog, setPortalAccessDialog] = useState(false);
  const [portalAccess, setPortalAccess] = useState(false);

  const [activeUsersColumn] = useState(['employeeStatus']);
  const [columnFilters, setColumnFilters] = useLocalStorage('usersColumnFilters', {});

  //to differentiate between popup and inline editing
  const [editingMode, setEditingMode] = useState(null);
  const editingModeRef = useRef(null);
  const [editingCellIds, setEditingCellIds] = useState([]);
  const [editingRowIds, setEditingRowIds] = useState([]);
  const [addedRows, setAddedRows] = useState([]);
  const [validationStatus, setValidationStatus] = useState({});

  const changeEditingMode = useCallback((mode) => {
    editingModeRef.current = mode;
    setEditingMode(mode);
  }, []);

  const startPopupEditing = useCallback(() => {
    setEditingCellIds([]);
    changeEditingMode('popup');
  }, [changeEditingMode]);

  const openUserDetails = useCallback(
    (rowId) => {
      setEditingCellIds([]);
      changeEditingMode('popup');
      setEditingRowIds([rowId]);
    },
    [changeEditingMode]
  );

  const editingStateColumnExtensions = useMemo(
    () => columns.map(({ name }) => ({ columnName: name, editingEnabled: INLINE_COLUMNS.includes(name) })),
    [columns]
  );

  const availableColumnValues = useMemo(
    () => ({
      userRole: userRoleRecords || [],
      vendorRef: vendorRefs || [],
      itemRef: itemRefs || [],
      taxType: taxTypeRecords || []
    }),
    [itemRefs, taxTypeRecords, userRoleRecords, vendorRefs]
  );

  const deleteRows = (deletedIds) => {
    const rowsForDelete = rows.slice();
    deletedIds.forEach((rowId) => {
      const index = rowsForDelete.findIndex((row) => row.id === rowId);
      if (index > -1) {
        rowsForDelete.splice(index, 1);
      }
    });
    return rowsForDelete;
  };

  //changed  only inline editing is handled.
  const commitChanges = ({ added, changed, deleted }) => {
    let changedRows;

    // PopupEditing owns creation and inserts the server-returned employee.
    // DevExpress still reports `added` when its draft row is committed; do not
    // insert that draft locally or the new employee appears twice.
    if (added) changedRows = undefined;

    if (changed) {
      const newValidationStatus = validateChanges(changed, validationStatus);
      setValidationStatus(newValidationStatus);

      if (hasValidationErrors(changed, newValidationStatus)) {
        toast.error('Required field cannot be empty.');
        return; // stop: no row merge, no backend call
      }

      changedRows = rows.map((row) => (changed[row.id] ? { ...row, ...changed[row.id] } : row));

      // Popup mode is active iff a row is currently open in the popup editor.
      // Anything else that reaches commitChanges with `changed` is inline.
      const isPopupCommit = editingRowIds.length > 0;

      if (!isPopupCommit) {
        const updates = Object.keys(changed).map((rowId) => {
          const updatedRow = changedRows.find((row) => String(row.id) === String(rowId));
          const updatedEmployeeId = updatedRow.id;

          const employeePayload = {
            // eslint-disable-next-line no-undef
            token: localStorage.getItem('token'),
            employeeId: updatedEmployeeId,
            updateUserId: updatedEmployeeId,
            ...changed[rowId]
          };

          const isLinkEmployeeUpdated = isLinkEmployeeChange(employeePayload);

          //only send changed field, update for link employee
          if (isLinkEmployeeUpdated) {
            const url = Constants.BASE_URL + 'linkEmployee/updateLinkEmployee';
            return axios.put(url, employeePayload);
          }

          //update for keypay employee
          if (updatedRow.KeyPayEmployeeId) {
            const url = Constants.BASE_URL + `keyPayEmployee/updateOneKeyPayEmployee/${updatedRow.KeyPayEmployeeId}`;
            return axios.post(url, employeePayload);
          }

          // change modified field to update to local db model field
          const localEmployeePayload = { ...employeePayload };
          if (employeePayload.userRole !== undefined) {
            localEmployeePayload.type = employeePayload.userRole;
          }
          if (employeePayload.Phone !== undefined) {
            localEmployeePayload.PrimaryPhone = { FreeFormNumber: employeePayload.Phone };
            localEmployeePayload.Mobile = { FreeFormNumber: employeePayload.Phone };
          }
          if (employeePayload.DisplayName !== undefined) {
            const displayNameParts = employeePayload.DisplayName.trim().split(/\s+/);
            localEmployeePayload.FirstName = displayNameParts.shift() || '';
            localEmployeePayload.FamilyName = displayNameParts.join(' ');
          }

          const url = Constants.BASE_URL + 'employee/updateEmployee';
          return axios.post(url, localEmployeePayload);
        });

        //eslint-disable-next-line no-undef
        Promise.all(updates)
          .then((responses) => {
            const steps = responses.flatMap((response) => response?.data?.steps || []);
            const failedSteps = steps.filter((step) => step.status === 'failed');

            if (failedSteps.length > 0) {
              if (failedSteps.some((step) => step.name === 'verd')) {
                setRows(rows);
              }
              const details = failedSteps.map((step) => `${step.name}: ${step.message}`).join('; ');

              toast.error(`Partially updated user. ${details}`);
              return;
            }

            toast.success('Successfully updated user');
          })
          .catch((error) => {
            setRows(rows);
            const steps = error?.response?.data?.steps || [];
            const failedStep = steps.find((step) => step.status === 'failed');
            const message = failedStep
              ? `${failedStep.name}: ${failedStep.message}`
              : error?.response?.data?.message || error?.message || 'Could not update employee';
            toast.error(`Could not fully update user. ${message}`);
          });
      }
    }

    // If delete button clicked, confirm before removing the row.
    // If the user cancels the confirm dialog, do nothing — do NOT call setRows.
    if (deleted) {
      // eslint-disable-next-line no-undef
      if (!window.confirm('Are you sure you want to delete this user?')) {
        return; // User cancelled — bail out, rows stay unchanged
      }

      changedRows = deleteRows(deleted);

      employeeService
        .deleteEmployee(deleted[0])
        .then(() => {
          toast.success('Successfully deleted user');
        })
        .catch((error) => {
          toast.error(error?.message || 'Could not delete user');
        });
    }

    // Only update rows if something actually changed
    if (changedRows !== undefined) {
      setRows(changedRows);
    }
  };

  const exporterRef = useRef();
  const startExport = useCallback(
    (options) => {
      exporterRef.current.exportGrid(options);
    },
    [exporterRef]
  );

  const filteredRows = useMemo(() => {
    return rows.filter((row) => {
      // Filter by employee status
      // if (filters.employeeStatus.length > 0) {
      //   const employeeStatus = row.portalAccess ? 'Active' : 'Inactive';
      //   if (!filters.employeeStatus.includes(employeeStatus)) {
      //     return false;
      //   }
      // }

      // Column header filters
      for (const columnName of Object.keys(columnFilters)) {
        const activeVals = columnFilters[columnName];
        if (activeVals && activeVals.length > 0) {
          const rowVal = getRowCellValue(row, columnName);
          if (!activeVals.includes(rowVal)) {
            return false;
          }
        }
      }

      return true;
    });
  }, [rows, columnFilters]);

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '1rem' }}>
        <div>Total rows selected: {selection.length}</div>
      </div>

      {/* <UserFilterToolbar filters={filters} onFilterChange={setFilters} /> */}
      <HeaderFilterContext.Provider value={{ rows, columnFilters, onFilterChange: setColumnFilters }}>
        <Paper>
          <Grid rows={filteredRows} columns={columns} getRowId={getRowId}>
            <EditingState
              addedRows={addedRows}
              onAddedRowsChange={(newAddedRows) => {
                setAddedRows(newAddedRows);
                if (newAddedRows.length) {
                  setEditingCellIds([]);
                  changeEditingMode(null);
                }
              }}
              editingCellIds={editingCellIds}
              onEditingCellIdsChange={(cellIds) => {
                if (editingModeRef.current !== 'popup' || editingModeRef.current === null) {
                  const editableCellIds = cellIds.filter(({ columnName }) => INLINE_COLUMNS.includes(columnName));
                  changeEditingMode(editableCellIds.length ? 'inline' : null);
                  setEditingCellIds(editableCellIds);
                }
              }}
              editingRowIds={editingRowIds}
              onEditingRowIdsChange={(rowIds) => {
                setEditingRowIds(rowIds);
                if (rowIds.length) setEditingCellIds([]);
              }}
              onCommitChanges={commitChanges}
              columnExtensions={editingStateColumnExtensions}
            />
            <LookupFormatter
              for={['userRole', 'vendorRef', 'itemRef', 'taxType']}
              availableColumnValues={availableColumnValues}
            />
            <ActiveStatusProvider for={activeUsersColumn} />

            <PagingState
              currentPage={currentPage}
              onCurrentPageChange={setCurrentPage}
              pageSize={pageSize}
              onPageSizeChange={setPageSize}
            />

            <SortingState sorting={sorting} onSortingChange={setSorting} />
            <IntegratedSorting />

            <SearchState value={searchValue} onValueChange={setSearchState} />
            <IntegratedFiltering />
            <SelectionState selection={selection} onSelectionChange={setSelection} />

            <IntegratedPaging />

            <IntegratedSelection />
            <DragDropProvider />

            <Table
              rowComponent={(props) => <ClickableUserRow {...props} onOpen={openUserDetails} />}
              cellComponent={(props) => (
                <FocusableCell
                  {...props}
                  validationStatus={validationStatus}
                  setValidationStatus={setValidationStatus}
                />
              )}
              containerComponent={ResponsiveTableContainer}
              headComponent={StickyTableHead}
            />

            <TableColumnReordering order={columnOrder} onOrderChange={setColumnOrder} />
            <TableColumnResizing columnWidths={columnWidths} onColumnWidthsChange={setColumnWidths} />
            <TableHeaderRow showSortingControls cellComponent={HeaderFilterCell} />

            <TableColumnVisibility
              hiddenColumnNames={hiddenColumnNames}
              onHiddenColumnNamesChange={setHiddenColumnNames}
            />
            <TableSelection showSelectAll />

            {/* Pass the custom root to the Toolbar */}
            <Toolbar rootComponent={CustomToolbarRoot} />

            <SearchPanel />
            <ColumnChooser />

            {/* Edit and Delete are shown in a kebab menu per row. Add works normally in the header. */}
            <TableEditColumn
              width={90}
              showAddCommand
              showEditCommand
              showDeleteCommand
              cellComponent={(props) => <KebabCommandCell {...props} onPopupEditStart={startPopupEditing} />}
            />

            <TableInlineCellEditing startEditAction="click" selectTextOnEditStart={true} />

            <PopupEditing
              popupComponent={Popup}
              userTabIndex={userTabIndex}
              setUserTabIndex={setUserTabIndex}
              vendorRecords={vendorRecords}
              keyPayEmployeeRecords={keyPayEmployeeRecords}
              selectedEmployeeToRegister={selectedEmployeeToRegister}
              setSelectedEmployeeToRegister={setSelectedEmployeeToRegister}
              setRows={setRows}
              rows={rows}
              selectedUserType={selectedUserType}
              setSelectedUserType={setSelectedUserType}
              approvalRequired={approvalRequired}
              setApprovalRequired={setApprovalRequired}
              selectedApprover={selectedApprover}
              setSelectedApprover={setSelectedApprover}
              portalAccessDialog={portalAccessDialog}
              setPortalAccessDialog={setPortalAccessDialog}
              portalAccess={portalAccess}
              setPortalAccess={setPortalAccess}
              editingMode={editingMode}
              setEditingMode={changeEditingMode}
            />

            <PagingPanel pageSizes={pageSizes} />
            <ExportPanel startExport={startExport} />
          </Grid>
          <GridExporter
            ref={exporterRef}
            rows={filteredRows}
            columns={columns}
            hiddenColumnNames={hiddenColumnNames}
            selection={selection}
            onSave={onSave}
            getRowId={getRowId}
          />
        </Paper>
      </HeaderFilterContext.Provider>
    </div>
  );
};

export default UserTable;

UserTable.propTypes = {
  rows: PropTypes.array,
  setRows: PropTypes.func,
  vendorRefs: PropTypes.array,
  taxTypeRecords: PropTypes.array,
  userRoleRecords: PropTypes.array,
  itemRefs: PropTypes.array
};
